import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from '../../../../util/reactIntl';
import RatingStars from '../RatingStars/RatingStars';
import css from './RatingSummary.module.css';

/**
 * Calculate rating statistics from reviews
 */
const calculateRatingStats = reviews => {
  if (!reviews || reviews.length === 0) {
    return {
      average: 0,
      distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
      total: 0,
    };
  }

  const ratings = reviews
    .map(r => r.attributes?.rating)
    .filter(r => r != null && r >= 1 && r <= 5);

  const total = ratings.length;
  if (total === 0) {
    return {
      average: 0,
      distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
      total: 0,
    };
  }

  const sum = ratings.reduce((acc, r) => acc + r, 0);
  const average = (sum / total).toFixed(1);

  const distribution = ratings.reduce(
    (acc, rating) => {
      acc[rating] = (acc[rating] || 0) + 1;
      return acc;
    },
    { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
  );

  return {
    average: parseFloat(average),
    distribution,
    total,
  };
};

const BasedOnReviewsLine = ({ count, onReviewsClick }) => {
  const msg = (
    <FormattedMessage
      id="StoreProfilePage.reviews.basedOnReviews"
      defaultMessage="based on {count} reviews"
      values={{ count }}
    />
  );
  if (onReviewsClick) {
    return (
      <button
        type="button"
        className={`${css.ratingText} ${css.reviewsLinkButton}`}
        onClick={onReviewsClick}
      >
        {msg}
      </button>
    );
  }
  return <div className={css.ratingText}>{msg}</div>;
};

BasedOnReviewsLine.propTypes = {
  count: PropTypes.number.isRequired,
  onReviewsClick: PropTypes.func,
};

const AverageSkeleton = () => (
  <div className={css.skeletonAverageBlock} aria-hidden="true">
    <div className={css.skeletonScore} />
    <div className={css.skeletonStarsRow}>
      {[1, 2, 3, 4, 5].map(i => (
        <div key={i} className={css.skeletonStarDot} />
      ))}
    </div>
  </div>
);

const BreakdownSkeleton = () => (
  <div className={css.ratingBreakdown} aria-hidden="true">
    {[5, 4, 3, 2, 1].map(star => (
      <div key={star} className={css.ratingRow}>
        <div className={css.skeletonLabel} />
        <div className={css.skeletonBarTrack}>
          <div className={css.skeletonBarFill} />
        </div>
        <div className={css.skeletonCount} />
      </div>
    ))}
  </div>
);

/**
 * RatingSummary — full layout always; optional skeleton when distribution data is still loading.
 */
const RatingSummary = ({
  reviews,
  totalReviewsCount,
  onReviewsClick,
  distributionIncomplete = false,
  summaryData = null,
}) => {
  const stats = useMemo(() => calculateRatingStats(reviews), [reviews]);
  const summaryReviewCount = Number(summaryData?.reviewCount);
  const summaryAverageRating = Number(summaryData?.averageRating);
  const summaryDistribution = summaryData?.starCounts || null;
  const hasSummaryData =
    Number.isFinite(summaryReviewCount) &&
    summaryReviewCount >= 0 &&
    Number.isFinite(summaryAverageRating) &&
    summaryAverageRating >= 0 &&
    summaryDistribution &&
    typeof summaryDistribution === 'object';

  const reviewCount = hasSummaryData
    ? Math.floor(summaryReviewCount)
    : Number.isFinite(totalReviewsCount)
      ? totalReviewsCount
      : stats.total;

  const normalizedSummaryDistribution = hasSummaryData
    ? {
      5: Math.max(0, Number(summaryDistribution[5] ?? summaryDistribution['5'] ?? 0)),
      4: Math.max(0, Number(summaryDistribution[4] ?? summaryDistribution['4'] ?? 0)),
      3: Math.max(0, Number(summaryDistribution[3] ?? summaryDistribution['3'] ?? 0)),
      2: Math.max(0, Number(summaryDistribution[2] ?? summaryDistribution['2'] ?? 0)),
      1: Math.max(0, Number(summaryDistribution[1] ?? summaryDistribution['1'] ?? 0)),
    }
    : stats.distribution;

  const displayAverage = hasSummaryData
    ? Number(summaryAverageRating.toFixed(1))
    : stats.average;

  const showSkeletons = !hasSummaryData && distributionIncomplete;

  if (stats.total === 0 && reviewCount === 0) {
    return null;
  }

  const maxCount = Math.max(...Object.values(normalizedSummaryDistribution));
  const showRealAverage =
    (hasSummaryData && reviewCount > 0) ||
    (!showSkeletons && stats.total > 0);

  return (
    <div className={css.ratingSummary}>
      <h4 className={css.title}>
        <FormattedMessage
          id="StoreProfilePage.reviews.averageRating"
          defaultMessage="Average Rating"
        />
      </h4>
      <div className={css.averageRating}>
        {showSkeletons ? (
          <AverageSkeleton />
        ) : showRealAverage ? (
          <>
            <div className={css.ratingNumber}>{displayAverage}</div>
            <RatingStars rating={displayAverage} />
          </>
        ) : (
          <>
            <div className={css.ratingNumber}>—</div>
            <RatingStars rating={0} />
          </>
        )}
        <BasedOnReviewsLine count={reviewCount} onReviewsClick={onReviewsClick} />
      </div>

      {showSkeletons ? (
        <BreakdownSkeleton />
      ) : (
        <div className={css.ratingBreakdown}>
          {[5, 4, 3, 2, 1].map(starRating => {
            const count = normalizedSummaryDistribution[starRating] || 0;
            const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;

            return (
              <div key={starRating} className={css.ratingRow}>
                <span className={css.ratingLabel}>
                  <span>{starRating}</span>★
                </span>
                <div className={css.ratingBar}>
                  <div
                    className={css.ratingFill}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
                <span className={css.reviewCount}>
                  <FormattedMessage
                    id="StoreProfilePage.reviews.reviewsCount"
                    defaultMessage="{count}"
                    values={{ count }}
                  />
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

RatingSummary.propTypes = {
  reviews: PropTypes.arrayOf(PropTypes.object).isRequired,
  totalReviewsCount: PropTypes.number,
  onReviewsClick: PropTypes.func,
  distributionIncomplete: PropTypes.bool,
  summaryData: PropTypes.shape({
    averageRating: PropTypes.number,
    reviewCount: PropTypes.number,
    starCounts: PropTypes.shape({
      1: PropTypes.number,
      2: PropTypes.number,
      3: PropTypes.number,
      4: PropTypes.number,
      5: PropTypes.number,
    }),
  }),
};

export default RatingSummary;
